/**
 * Q&A Page - Interactive question-answer interface
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Lightbulb } from 'lucide-react';
import qaService, { CurrentQuestion } from '../services/qaService';

const QAPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [currentQuestion, setCurrentQuestion] = useState<CurrentQuestion | null>(null);
  const [answer, setAnswer] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAgentComplete, setShowAgentComplete] = useState(false);

  const loadCurrentQuestion = async () => {
    try {
      const question = await qaService.getCurrentQuestion(sessionId!);

      if (question.all_complete) {
        // Q&A complete, navigate to reports
        navigate(`/reports/${sessionId}`);
        return;
      }

      setCurrentQuestion(question);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load question');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentQuestion();
  }, [sessionId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!answer.trim()) {
      setError('Please provide an answer');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await qaService.submitAnswer(sessionId!, {
        agent_id: currentQuestion!.agent_id,
        answer: answer.trim(),
      });

      // Clear the answer field
      setAnswer('');

      // Check if agent complete
      if (response.agent_complete) {
        setShowAgentComplete(true);
        setTimeout(() => {
          setShowAgentComplete(false);
          // Load next question
          loadCurrentQuestion();
        }, 2000);
      } else if (response.all_agents_complete) {
        // All done, navigate to reports
        navigate(`/reports/${sessionId}`);
      } else {
        // Update with next question
        if (response.next_question && currentQuestion) {
          setCurrentQuestion({
            question: response.next_question,
            agent_id: response.agent_id!,
            agent_name: response.agent_name!,
            question_number: response.question_number!,
            total_questions: response.total_questions!,
            agent_number: response.agent_number!,
            total_agents: response.total_agents!,
          });
        }
      }

      setIsSubmitting(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit answer');
      setIsSubmitting(false);
    }
  };

  const charCount = answer.length;
  const showWarning = charCount > 5000;

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Loading questions...</h2>
      </div>
    );
  }

  if (error && !currentQuestion) {
    return (
      <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          padding: '20px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
        }}>
          <h3>Error</h3>
          <p>{error}</p>
          <button
            onClick={() => navigate('/')}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Return to Home
          </button>
        </div>
      </div>
    );
  }

  if (showAgentComplete) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{
          padding: '40px',
          backgroundColor: '#d4edda',
          color: '#155724',
          border: '2px solid #c3e6cb',
          borderRadius: '8px',
          maxWidth: '600px',
          margin: '0 auto',
        }}>
          <h2 style={{ fontSize: '2rem', marginBottom: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}><CheckCircle2 size={32} /> Agent Complete!</h2>
          <p style={{ fontSize: '1.2rem' }}>
            Q&A session with <strong>{currentQuestion?.agent_name}</strong> is complete.
          </p>
          <p style={{ marginTop: '8px', color: '#666' }}>
            Moving to next agent...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '40px', maxWidth: '900px', margin: '0 auto' }}>
      {/* Progress Header */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={{ fontSize: '2rem', margin: 0 }}>Q&A Session</h1>
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#007bff',
            color: 'white',
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
          }}>
            Agent {currentQuestion?.agent_number} of {currentQuestion?.total_agents}
          </div>
        </div>

        <div style={{
          padding: '16px',
          backgroundColor: '#e3f2fd',
          borderLeft: '4px solid #2196f3',
          borderRadius: '4px',
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>
            {currentQuestion?.agent_name}
          </h3>
          <div style={{ fontSize: '0.9rem', color: '#666' }}>
            Question {currentQuestion?.question_number} of {currentQuestion?.total_questions}
          </div>
        </div>
      </div>

      {/* Question */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          padding: '24px',
          backgroundColor: '#f8f9fa',
          border: '2px solid #dee2e6',
          borderRadius: '8px',
        }}>
          <div style={{ fontSize: '0.9rem', color: '#6c757d', marginBottom: '8px', fontWeight: 'bold' }}>
            QUESTION
          </div>
          <p style={{ fontSize: '1.2rem', margin: 0, lineHeight: '1.6' }}>
            {currentQuestion?.question}
          </p>
        </div>
      </div>

      {/* Answer Form */}
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', fontSize: '1.1rem' }}>
            Your Answer
          </label>
          <textarea
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder="Provide a detailed answer to help the specialist understand your situation..."
            style={{
              width: '100%',
              minHeight: '200px',
              padding: '16px',
              fontSize: '1rem',
              border: `2px solid ${showWarning ? '#ffc107' : '#ced4da'}`,
              borderRadius: '4px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px' }}>
            <span style={{ fontSize: '0.9rem', color: '#6c757d' }}>
              {charCount} characters
            </span>
            {showWarning && (
              <span style={{ fontSize: '0.9rem', color: '#ff6f00', fontWeight: 'bold' }}>
                <AlertTriangle size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Your answer is quite long ({charCount} characters). Consider being more concise for better analysis quality.
              </span>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '16px',
          }}>
            {error}
          </div>
        )}

        {/* Submit Button */}
        <div style={{ display: 'flex', gap: '16px' }}>
          <button
            type="submit"
            disabled={isSubmitting || !answer.trim()}
            style={{
              flex: 1,
              padding: '16px',
              fontSize: '1.1rem',
              backgroundColor: isSubmitting || !answer.trim() ? '#ccc' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isSubmitting || !answer.trim() ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Answer & Continue'}
          </button>
        </div>
      </form>

      {/* Helper Text */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        backgroundColor: '#fff3cd',
        border: '1px solid #ffeaa7',
        borderRadius: '4px',
        fontSize: '0.9rem',
      }}>
        <strong><Lightbulb size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Tip:</strong> Be as specific as possible. The more detail you provide, the better the specialist can tailor their strategic recommendations to your needs.
      </div>
    </div>
  );
};

export default QAPage;
